import * as React from "react";
import { Button, Field, Input, Link, MessageBar, MessageBarBody, Spinner, makeStyles, tokens } from "@fluentui/react-components";

import { ApiError, createSession } from "../lib/api";
import { openExternal } from "../lib/officeDialog";
import type { Session } from "../lib/session";

const SIGN_UP_URL = "https://valoon.chat";

const useStyles = makeStyles({
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, margin: 0 },
  footer: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" },
});

export interface CredentialFormProps {
  /** Called with the session once the BFF has verified the credentials. */
  onSignedIn: (session: Session) => void;
  /** Rendered as a secondary action; omitted when there is nothing to go back to. */
  onCancel?: () => void;
}

/**
 * The sign-in form.
 *
 * Rendered either inside the Office dialog or inline in the task pane, so it
 * owns no layout beyond its own stack — the two hosts differ in width and in
 * whether a cancel action makes sense.
 */
export const CredentialForm: React.FC<CredentialFormProps> = ({ onSignedIn, onCancel }) => {
  const styles = useStyles();
  const [clientId, setClientId] = React.useState("");
  const [apiKey, setApiKey] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Trimmed here as well as on the server: pasting from a password manager
      // routinely drags a trailing space along, and the user should not have
      // to discover that from a rejection.
      onSignedIn(await createSession(clientId.trim(), apiKey.trim()));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  const disabled = busy || clientId.trim() === "" || apiKey.trim() === "";

  return (
    <form className={styles.form} onSubmit={submit}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Field label="Client ID" required>
        <Input
          value={clientId}
          onChange={(_, data) => setClientId(data.value)}
          disabled={busy}
          autoComplete="off"
          spellCheck={false}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </Field>

      <Field label="API key" required>
        <Input
          type="password"
          value={apiKey}
          onChange={(_, data) => setApiKey(data.value)}
          disabled={busy}
          autoComplete="off"
        />
      </Field>

      <p className={styles.hint}>
        Both are in Valoon under your account settings. They are sent once to verify your account and are never
        stored in Outlook.
      </p>

      <Button appearance="primary" type="submit" disabled={disabled}>
        {busy ? <Spinner size="tiny" label="Verifying…" /> : "Connect"}
      </Button>

      <div className={styles.footer}>
        {onCancel && (
          <Button appearance="subtle" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        {/* Marketplace expects a visible way to sign up, not just sign in. */}
        <Link
          as="button"
          type="button"
          onClick={() => openExternal(SIGN_UP_URL)}
          inline
        >
          Don&apos;t have a Valoon account? Sign up
        </Link>
      </div>
    </form>
  );
};
